import { type CareerApplicationDetail, type CareerApplicationStatus } from '@utazzo/shared';
import { UzButton, UzDialog } from '@utazzo/ui';
import { Download, Mail, Phone } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { getCvUrl } from '../careers.api';
import { useUpdateApplicationStatus } from '../use-careers';

import { SoftPill } from '@/components/soft-pill';

/**
 * Read-and-act view of a single job application.
 *
 * Opened from the HR dashboard's recent-applicants table so a candidate can be
 * read, their CV downloaded and their stage moved without first navigating to
 * the Careers editor and hunting for the row. Shared rather than dashboard-only
 * so the Enquiries table can reuse it.
 */

const STATUS_PILL: Record<CareerApplicationStatus, { tone: string; dot: string; label: string }> = {
  RECEIVED: { tone: 'bg-sky-50 text-sky-700', dot: 'bg-sky-500', label: 'Received' },
  IN_REVIEW: { tone: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500', label: 'In review' },
  INTERVIEW: { tone: 'bg-violet-50 text-violet-700', dot: 'bg-violet-500', label: 'Interview' },
  OFFER: { tone: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', label: 'Offer' },
  REJECTED: { tone: 'bg-rose-50 text-rose-700', dot: 'bg-rose-500', label: 'Rejected' },
  WITHDRAWN: { tone: 'bg-zinc-100 text-zinc-600', dot: 'bg-zinc-400', label: 'Withdrawn' },
};

const STATUS_ORDER: CareerApplicationStatus[] = [
  'RECEIVED',
  'IN_REVIEW',
  'INTERVIEW',
  'OFFER',
  'REJECTED',
  'WITHDRAWN',
];

/** Opens the CV via a short-lived presigned URL - bytes are never inlined. */
export const downloadCv = async (id: string): Promise<void> => {
  try {
    const { url } = await getCvUrl(id);
    window.open(url, '_self', 'noopener');
  } catch {
    toast.error('Could not open the CV. It may have been removed.');
  }
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div className="min-w-0">
    <dt className="text-xs font-medium text-(--fg-faint)">{label}</dt>
    <dd className="mt-0.5 truncate text-(--fg)">{children}</dd>
  </div>
);

interface Props {
  applicant: CareerApplicationDetail | null;
  onClose: () => void;
  /** Show the stage buttons. False for read-only callers. */
  canWrite?: boolean;
}

export const ApplicantDetailDialog = ({
  applicant,
  onClose,
  canWrite = false,
}: Props): React.JSX.Element => {
  const updateStatus = useUpdateApplicationStatus();
  const pill = applicant ? STATUS_PILL[applicant.status] : null;

  return (
    <UzDialog
      open={applicant !== null}
      onOpenChange={(o) => !o && onClose()}
      size="2xl"
      title={applicant?.name ?? 'Applicant'}
      description={applicant ? `Applied for ${applicant.positionTitle}` : ''}
      footer={
        <>
          <UzButton variant="ghost" onClick={onClose}>
            Close
          </UzButton>
          {applicant && (
            <UzButton
              onClick={() => void downloadCv(applicant.id)}
              disabled={!applicant.cvFileName}
              title={applicant.cvFileName || 'No CV on file'}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Download CV
            </UzButton>
          )}
        </>
      }
    >
      {applicant && (
        <div className="flex flex-col gap-5 text-sm">
          <div>
            {pill && (
              <SoftPill className={pill.tone} dot dotClassName={pill.dot}>
                {pill.label}
              </SoftPill>
            )}
          </div>

          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Field label="Email">
              {/* mailto / tel so the contact details are one click, not a
                  copy-paste - this table is the HR desk's call list. */}
              <a
                href={`mailto:${applicant.email}`}
                className="inline-flex items-center gap-1.5 hover:text-(--primary) hover:underline"
              >
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {applicant.email}
              </a>
            </Field>
            <Field label="Phone">
              <a
                href={`tel:${applicant.phone}`}
                className="inline-flex items-center gap-1.5 hover:text-(--primary) hover:underline"
              >
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {applicant.phone || '—'}
              </a>
            </Field>
            <Field label="Role applied for">{applicant.positionTitle}</Field>
            <Field label="Applied on">
              {new Date(applicant.createdAt).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Field>
            <Field label="CV">{applicant.cvFileName || 'Not provided'}</Field>
          </dl>

          {canWrite && (
            <section>
              <h4 className="mb-2 text-xs font-semibold tracking-wide text-(--fg-muted) uppercase">
                Move to stage
              </h4>
              <div className="flex flex-wrap gap-2">
                {STATUS_ORDER.filter((s) => s !== applicant.status).map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={updateStatus.isPending}
                    onClick={() => updateStatus.mutate({ id: applicant.id, status: s })}
                    className="cursor-pointer rounded-lg border border-(--border) px-2.5 py-1.5 text-xs font-medium transition-colors hover:border-(--primary) hover:text-(--primary) disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {STATUS_PILL[s].label}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </UzDialog>
  );
};
